<template>
  <RequestWizardStep name="Other Details"
    :complete="isComplete"
  >
    <ContentCard title="The FOIA permits CMS to charge fees to FOIA requesters.">
      <p class="lead">For noncommercial requesters, CMS may charge only for the actual cost of
        searching for records and the cost of making copies.</p>

      <AlertMessage type="info">
        <p>CMS will not charge if the total processing cost is less than $25.
          If the total processing cost is estimated to exceed the amount specified below,
          CMS will notify the requestor before proceeding with processing the request.
          If a fee waiver is denied, CMS will contact you to determine if you would like
          to proceed with your request.
        </p>

        For more information on processing fees, please read our
        <a target="_blank" href="https://www.cms.gov/Regulations-and-Guidance/Legislation/FOIA/downloads/FOIAFAQ.pdf"
        >FOIA FAQs</a>.
      </AlertMessage>

      <div class="row">
        <RadioButtons
          :options="[
            {
              value: false,
              label: 'I am willing to pay fees for this request up to the following amount:'
            },
            {
              value: true,
              label: 'I request a waiver or reduction of all fees for this request.'
            }
          ]"
          class="col-lg-6"
          required
          v-model="otherDetails.feeWaiverRequested"
        />

        <StepSelection class="col-lg-6 row ms-4 ms-lg-0">
          <div v-show-slide="otherDetails.feeWaiverRequested === false"
            class="w-auto"
          >
            <CurrencyBox
              label="Max Fee Amount"
              :rules="{
                required: requiredIf(() => otherDetails.feeWaiverRequested === false)
              }"
              v-model.number="otherDetails.maxFeeAmount"
              v-model:setFocus="focusOnFeeAmount"
            />
          </div>

          <div v-show-slide="otherDetails.feeWaiverRequested">
            <TextAreaField
              label="Waiver Justification"
              class="mb-3"
              rows="3"
              :rules="{
                required: requiredIf(() => otherDetails.feeWaiverRequested),
                max: maxLength(2500)
              }"
              v-model="otherDetails.feeWaiverJustification"
              v-model:setFocus="focusOnWaiverJustification"
            />
          </div>
        </StepSelection>
      </div>

      <div class="mt-4">
        <AlertMessage type="info">
          Expedited processing will be granted only under specific circumstances.
          Please see the
          <a target="_blank"
            href="https://www.cms.gov/Regulations-and-Guidance/Legislation/FOIA/downloads/FOIAFAQ.pdf"
          >FOIA FAQs</a> to determine if you qualify for expedited processing.
          If you believe these circumstances apply, then select "Yes" below and provide
          a justification.
        </AlertMessage>

        <div class="row">
          <RadioButtons
            label="I request expedited processing:"
            :options="[
              { value: false, label: 'No' },
              { value: true, label: 'Yes' }
            ]"
            class="col-lg-6"
            required
            v-model="otherDetails.expeditedProcessingRequested"
          />

          <StepSelection class="col-lg-6 row ms-4 ms-lg-0">
            <div v-show-slide="otherDetails.expeditedProcessingRequested">
              <TextAreaField
                label="Expedition Justification"
                class="mb-3"
                rows="3"
                :rules="{
                  required: requiredIf(() => otherDetails.expeditedProcessingRequested),
                  max: maxLength(2500)
                }"
                v-model="otherDetails.expeditionJustification"
                v-model:setFocus="focusOnExpeditionJustification"
              />
            </div>
          </StepSelection>
        </div>
      </div>

      <div>
        <p class="lead"></p>

        <div class="row">
          <RadioButtons
            label="I request certification for the responsive records. Additional fees will be
              invoiced for record certification. This is in addition to other fees assessed."
            :options="[
              { value: false, label: 'No' },
              { value: true, label: 'Yes' }
            ]"
            class="col-12"
            required
            v-model="otherDetails.recordCertificationRequested"
          />

          <StepSelection class="col-12 row ms-3 mt-3">
            <div v-show-slide="otherDetails.recordCertificationRequested">
              <CheckBox v-model="otherDetails.certificationIsNotAParty"
                :required="otherDetails.recordCertificationRequested"
                v-model:setFocus="focusOnCertIsNotAParty"
              >
                The request is for a proceeding in which the United States Government or
                HHS is not a party.</CheckBox>

              <p>Certification is requested in order for the responsive records to be admitted
                into evidence. This condition is met if the Freedom of Information Act (FOIA)
                request is accompanied by a federal or non-federal document subpoena or the
                requester states that certification is required so that the responsive records
                can be admitted into evidence in a judicial proceeding and they provide the
                civil or criminal case number, along with the name of the court.</p>

              <div class="row g-3">
                <TextBox
                  label="Civil or Criminal Case Number"
                  class="col-md-6"
                  :rules="{
                    required: requiredIf(() => otherDetails.recordCertificationRequested),
                    max: maxLength(30)
                  }"
                  v-model="otherDetails.civilOrCriminalCaseNumber"
                />

                <TextBox
                  label="Name of the Court"
                  class="col-md-6"
                  :rules="{
                    required: requiredIf(() => otherDetails.recordCertificationRequested),
                    max: maxLength(100)
                  }"
                  v-model="otherDetails.nameOfTheCourt"
                />
              </div>
            </div>
          </StepSelection>
        </div>
      </div>
    </ContentCard>
  </RequestWizardStep>
</template>

<script>
import { computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import AlertMessage from '@/components/AlertMessage';
import { CurrencyBox, TextArea as TextAreaField, TextBox } from '@/components/formFields/common';
import CheckBox from '@/components/formFields/CheckBox';
import RadioButtons from '@/components/formFields/RadioButtons';
import RequestWizardStep from '@/components/requestWizard/RequestWizardStep';
import StepSelection from '@/components/requestWizard/StepSelection';
import { maxLength, requiredIf } from '@/validators';
import { request } from '../requestWizardState';

export default {
  setup: () => {
    const v$ = useVuelidate();
    const otherDetails = computed(() => request.value.common.otherDetails);

    return {
      otherDetails,
      maxLength,
      requiredIf,
      v$
    };
  },

  components: {
    AlertMessage,
    CheckBox,
    CurrencyBox,
    RadioButtons,
    RequestWizardStep,
    StepSelection,
    TextAreaField,
    TextBox
  },

  data: () => ({
    focusOnCertIsNotAParty: false,
    focusOnExpeditionJustification: false,
    focusOnFeeAmount: false,
    focusOnWaiverJustification: false
  }),

  computed: {
    isComplete: ({ v$ }) => !v$.$invalid,
  },

  watch: {
    'otherDetails.feeWaiverRequested': function watchFeeWaiverRequested(val) {
      if (val) {
        this.otherDetails.maxFeeAmount = null;

        // Wait for field to become visible.
        setTimeout(() => { this.focusOnWaiverJustification = true; }, 500);
      } else {
        this.otherDetails.feeWaiverJustification = null;
        setTimeout(() => { this.focusOnFeeAmount = true; }, 500);
      }
    },

    'otherDetails.expeditedProcessingRequested': function watchExpeditedProcessingRequested(val) {
      if (val) {
        setTimeout(() => { this.focusOnExpeditionJustification = true; }, 500);
      } else {
        this.otherDetails.expeditionJustification = null;
      }
    },

    'otherDetails.recordCertificationRequested': function watchRecordCertificationRequested(val) {
      if (val) {
        setTimeout(() => { this.focusOnCertIsNotAParty = true; }, 500);
      } else {
        this.otherDetails.certificationIsNotAParty = false;
        this.otherDetails.civilOrCriminalCaseNumber = null;
        this.otherDetails.nameOfTheCourt = null;
      }
    }
  }
};
</script>
